import type { WorkflowGraphDefinition } from '../../services/workflowsApi.ts';

export type WorkflowCanvasPoint = { x: number; y: number };
export type WorkflowCanvasViewport = WorkflowGraphDefinition['presentation']['viewport'];
export type WorkflowCanvasBounds = WorkflowCanvasPoint & { width: number; height: number };
export const WORKFLOW_NODE_DRAG_TYPE = 'application/sminy-workflow-node';
export const WORKFLOW_DRAG_THRESHOLD = 6;

export function clientToWorkflowPoint(client: WorkflowCanvasPoint, rect: Pick<DOMRect, 'left' | 'top'>, viewport: WorkflowCanvasViewport, scroll: WorkflowCanvasPoint = { x: 0, y: 0 }): WorkflowCanvasPoint {
  return { x: (client.x - rect.left + scroll.x - viewport.x) / viewport.zoom, y: (client.y - rect.top + scroll.y - viewport.y) / viewport.zoom };
}

export function fitWorkflowBounds(bounds: WorkflowCanvasBounds[], width: number, height: number, padding = 48): WorkflowCanvasViewport | null {
  if (!bounds.length || width <= 0 || height <= 0) return null;
  const minX = Math.min(...bounds.map(b => b.x)), minY = Math.min(...bounds.map(b => b.y));
  const maxX = Math.max(...bounds.map(b => b.x + b.width)), maxY = Math.max(...bounds.map(b => b.y + b.height));
  const zoom = Math.max(.1, Math.min(1.5, (width - padding * 2) / Math.max(1, maxX - minX), (height - padding * 2) / Math.max(1, maxY - minY)));
  return { x: (width - (maxX - minX) * zoom) / 2 - minX * zoom, y: (height - (maxY - minY) * zoom) / 2 - minY * zoom, zoom };
}

export function zoomWorkflowAt(viewport: WorkflowCanvasViewport, zoom: number, center: WorkflowCanvasPoint): WorkflowCanvasViewport {
  const nextZoom = Math.max(.1, Math.min(1.5, zoom));
  return { x: center.x - (center.x - viewport.x) * nextZoom / viewport.zoom, y: center.y - (center.y - viewport.y) * nextZoom / viewport.zoom, zoom: nextZoom };
}

/** Cubic curves keep separate ports readable; anchors are presentation only. */
export function workflowCurve(start: WorkflowCanvasPoint, end: WorkflowCanvasPoint, from: 'right' | 'bottom' = 'right', to: 'left' | 'top' = 'left'): string {
  const bend = Math.max(48, Math.min(180, Math.abs(end.x - start.x) * .45 + Math.abs(end.y - start.y) * .15));
  const c1 = from === 'bottom' ? { x: start.x, y: start.y + bend } : { x: start.x + bend, y: start.y };
  const c2 = to === 'top' ? { x: end.x, y: end.y - bend } : { x: end.x - bend, y: end.y };
  return `M${start.x} ${start.y} C${c1.x} ${c1.y} ${c2.x} ${c2.y} ${end.x} ${end.y}`;
}

/** The approved tablet framing is local until the user moves/saves the camera. */
export function initialWorkflowViewport(viewport: WorkflowCanvasViewport, width: number): WorkflowCanvasViewport {
  return width >= 640 && width < 1280 && viewport.x === 0 && viewport.y === 0 && viewport.zoom === 1 ? { ...viewport, x: -184 } : viewport;
}
